/**
 * `Tooltip` — portal, flip + shift, hoverable, keyboard + touch.
 *
 * Replaces three in-tree mechanisms (spec: labels-review/ux.md §12):
 *   - the CSS-only tooltip class / data-attribute pattern
 *   - the previous `Tooltip` atom (`position: absolute` inside the trigger)
 *   - `TooltipPortal` inside `MandateDetailV3.tsx`
 *
 * One component, one behaviour:
 *   - renders the bubble into `document.body` with `position: fixed`, so
 *     scroll containers and `overflow: hidden` never clip it.
 *   - **flip** to the opposite side when the preferred side does not fit.
 *   - **shift** on the cross axis so the bubble stays ≥ 8px inside the
 *     viewport.
 *   - hides when the trigger scrolls out of its nearest scroll container.
 *   - 300ms open delay on hover, closes 100ms after leave, hoverable bubble
 *     (WCAG 1.4.13 "hoverable").
 *   - keyboard: opens on `:focus-visible`, closes on blur and Escape (WCAG
 *     1.4.13 "dismissible"). `role="tooltip"` + `aria-describedby`.
 *   - touch: `onClick` only (never `onClick` + `onTouchStart`, which caused
 *     the TooltipPortal double-toggle bug). Non-interactive triggers tap-to-
 *     toggle, taps elsewhere / scroll / second tap close. Interactive
 *     triggers do **not** open a tooltip on touch — their action runs.
 *   - size: `max-width: min(280px, 100vw - 16px)`, wraps.
 *   - z-index: single token `--z-tooltip: 1000` (globals.css).
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useC, useTheme } from '../../lib/theme.js';
import { useAnchoredPosition, type AnchorSide } from './useAnchoredPosition.js';

const sansFont = "'Geist', sans-serif";

export type TooltipSide = AnchorSide;

export interface TooltipProps {
  readonly children: ReactNode;
  readonly content: ReactNode;
  readonly side?: TooltipSide;
  readonly delay?: number;
  /** Passthrough class for the wrapper `<span>`. */
  readonly className?: string;
  /** Passthrough inline style for the wrapper `<span>`. */
  readonly style?: CSSProperties;
  /** Override the wrapper `display`. */
  readonly displayMode?: 'inline-flex' | 'inline' | 'block';
  /** Test id passthrough (the bubble gets `${testid}-bubble`). */
  readonly 'data-testid'?: string;
  /** Force the wrapper to never be a tab stop. Normally detected: the
   *  wrapper is focusable only when it neither contains nor sits inside an
   *  interactive element (spec §9.5 / §12.3: no nested tab stops). */
  readonly nonFocusable?: boolean;
  /** Accessible name for an icon-only trigger (applied to the wrapper when
   *  it is the focus target). */
  readonly 'aria-label'?: string;
}

const INTERACTIVE = 'button, a[href], input, select, textarea, summary, [role="button"], [role="link"], [role="tab"], [role="checkbox"], [role="switch"], [role="menuitem"], [tabindex]:not([tabindex="-1"]):not(.sill-tip-trigger)';

function isCoarsePointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(hover: none)').matches;
}

/** True when the wrapper contains, or is inside, an interactive element. On
 *  touch such a trigger performs its own action and never opens a tooltip,
 *  and with a keyboard the existing control is the focus target. */
function hasInteractive(el: HTMLElement): boolean {
  if (el.querySelector(INTERACTIVE) !== null) return true;
  const parent = el.parentElement;
  // A tooltip nested in another tooltip's trigger is hover-only too: the
  // outer trigger is the tab stop. (Other triggers are excluded from the
  // descendant query above, so the two decisions cannot flip each other.)
  return parent !== null && parent.closest(`${INTERACTIVE}, .sill-tip-trigger`) !== null;
}

export function Tooltip({
  children,
  content,
  side = 'top',
  delay = 300,
  className,
  style,
  displayMode = 'inline-flex',
  'data-testid': testid,
  nonFocusable = false,
  'aria-label': ariaLabel,
}: TooltipProps): JSX.Element {
  const { theme } = useTheme();
  const C = useC();
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  // Detected after mount: does the trigger own (or sit in) a control?
  const [interactive, setInteractive] = useState(nonFocusable);
  const openedByTap = useRef(false);
  const openedAt = useRef(0);
  // Focus that follows a pointer press is not keyboard focus: only
  // keyboard (:focus-visible-style) focus opens the bubble immediately.
  const pointerFocus = useRef(false);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();

  useLayoutEffect(() => {
    const el = triggerRef.current;
    setInteractive(nonFocusable || (el !== null && hasInteractive(el)));
  });

  const clearTimers = useCallback((): void => {
    if (openTimer.current !== null) clearTimeout(openTimer.current);
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);

  const close = useCallback((): void => {
    clearTimers();
    openedByTap.current = false;
    setOpen(false);
  }, [clearTimers]);

  const enabled = content !== null && content !== undefined && content !== false && content !== '';

  const show = useCallback((immediate: boolean): void => {
    clearTimers();
    if (!enabled) return;
    if (immediate) {
      setOpen(true);
      return;
    }
    openTimer.current = setTimeout(() => setOpen(true), delay);
  }, [clearTimers, delay, enabled]);

  const hideSoon = useCallback((): void => {
    if (openTimer.current !== null) clearTimeout(openTimer.current);
    openTimer.current = null;
    if (openedByTap.current) return; // tap-opened: stays until dismissed
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 100);
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  // Escape dismisses (WCAG 1.4.13). A tap or click outside dismisses. A
  // scroll dismisses a tap-opened bubble (spec §12.3 touch rules); a
  // hover/focus-opened bubble follows its trigger instead.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    const onDown = (e: PointerEvent | MouseEvent): void => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (triggerRef.current?.contains(t) === true || bubbleRef.current?.contains(t) === true) return;
      close();
    };
    const onScroll = (e: Event): void => {
      if (!openedByTap.current) return;
      // The tap itself can nudge a scroll (focus scroll-into-view, momentum).
      if (Date.now() - openedAt.current < 300) return;
      if (e.target instanceof Node && bubbleRef.current?.contains(e.target) === true) return;
      close();
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, close]);

  const pos = useAnchoredPosition(triggerRef, bubbleRef, { open, side });

  // When the trigger is an existing control, describe that control.
  useEffect(() => {
    if (!open || !interactive) return;
    const ctl = triggerRef.current?.querySelector<HTMLElement>(INTERACTIVE);
    if (ctl === null || ctl === undefined || ctl.hasAttribute('aria-describedby')) return;
    ctl.setAttribute('aria-describedby', id);
    return () => ctl.removeAttribute('aria-describedby');
  }, [open, interactive, id]);

  const inBubble = (t: EventTarget): boolean => t instanceof Node && bubbleRef.current?.contains(t) === true;

  const onMouseEnter = (): void => {
    if (isCoarsePointer()) return; // touch: emulated hover is ignored, onClick decides
    show(false);
  };
  const onMouseLeave = (): void => {
    if (isCoarsePointer()) return;
    hideSoon();
  };
  const onFocus = (e: React.FocusEvent<HTMLSpanElement>): void => {
    if (inBubble(e.target)) return;
    if (isCoarsePointer() && interactive) return;
    if (pointerFocus.current) return;
    show(true);
  };
  const onBlur = (e: React.FocusEvent<HTMLSpanElement>): void => {
    if (inBubble(e.target)) return;
    if (openedByTap.current) return;
    close();
  };
  // Touch: one handler only (onClick). Non-interactive triggers toggle; a
  // trigger with its own action runs it and shows no tooltip.
  const onClick = (e: React.MouseEvent<HTMLSpanElement>): void => {
    if (inBubble(e.target)) return;
    if (!isCoarsePointer() || interactive || !enabled) return;
    if (open) {
      close();
    } else {
      clearTimers();
      openedByTap.current = true;
      openedAt.current = Date.now();
      setOpen(true);
    }
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>): void => {
    if (e.key === 'Escape' && open) close();
  };

  const displayClass =
    displayMode === 'inline-flex' ? 'inline-flex'
    : displayMode === 'block' ? 'block'
    : 'inline';
  const focusTarget = !interactive && enabled;
  const wrapperClassName = [enabled ? 'sill-tip-trigger' : undefined, displayClass, className].filter(Boolean).join(' ');

  const bubble: ReactElement | null = open && enabled && typeof document !== 'undefined' ? (
    createPortal(
      <div
        ref={bubbleRef}
        id={id}
        role="tooltip"
        data-testid={testid !== undefined ? `${testid}-bubble` : undefined}
        data-placement={pos.side}
        onMouseEnter={() => { if (closeTimer.current !== null) { clearTimeout(closeTimer.current); closeTimer.current = null; } }}
        onMouseLeave={hideSoon}
        style={{
          position: 'fixed',
          top: pos.top,
          left: pos.left,
          visibility: pos.visible ? 'visible' : 'hidden',
          background: theme === 'dark' ? '#000' : '#1A1410',
          color: '#F2EEE8',
          fontFamily: sansFont,
          fontSize: 12,
          lineHeight: 1.5,
          letterSpacing: 'normal',
          textTransform: 'none',
          textAlign: 'left',
          padding: '8px 10px',
          borderRadius: 2,
          width: 'max-content',
          maxWidth: 'min(280px, calc(100vw - 16px))',
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          fontWeight: 400,
          pointerEvents: 'auto',
          zIndex: 'var(--z-tooltip, 1000)' as unknown as number,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          border: theme === 'dark' ? `1px solid ${C.border}` : 'none',
          cursor: 'default',
        }}
      >
        {content}
      </div>,
      document.body,
    )
  ) : null;

  return (
    <span
      ref={triggerRef}
      className={wrapperClassName}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onPointerDown={() => {
        pointerFocus.current = true;
        setTimeout(() => { pointerFocus.current = false; }, 0);
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={focusTarget ? 0 : undefined}
      aria-label={focusTarget ? ariaLabel : undefined}
      aria-describedby={open && focusTarget ? id : undefined}
      style={style}
      data-testid={testid}
    >
      {children}
      {bubble}
    </span>
  );
}
